import * as THREE from 'three';

// Weather and seasons for Toast Town.
// Season is deterministic from the world day. Weather is deterministic from the
// day + a daily cycle, so every player sees the same sky on the same in-game day.
// Outdoor scenes can attach a WeatherSystem to render particles (rain, snow,
// petals, leaves) and adjust fog/lighting tints.

import { getDay, getHour, onTimeChange } from './world.js';

export const SEASONS = ['spring', 'summer', 'autumn', 'winter'];

const SEASON_DAYS = 7; // each season lasts 7 in-game days

export function getSeason(day = getDay()){
  const i = Math.floor((day - 1) / SEASON_DAYS) % SEASONS.length;
  return SEASONS[i];
}

export function getSeasonProgress(day = getDay()){
  const i = Math.floor((day - 1) / SEASON_DAYS) % SEASONS.length;
  const d = ((day - 1) % SEASON_DAYS) + 1;
  return { season: SEASONS[i], index: i, dayInSeason: d, daysLeft: SEASON_DAYS - d + 1 };
}

const WEATHER_TYPES = ['clear', 'cloudy', 'rain', 'windy', 'snow'];

function dayHash(day){
  let h = 2166136261;
  let x = day * 374761393;
  x = (x ^ (x >>> 16)) >>> 0;
  x = (x * 668265263) >>> 0;
  x = (x ^ (x >>> 15)) >>> 0;
  h = (h ^ x) >>> 0;
  h = (h * 16777619) >>> 0;
  return h;
}

function randFrom(seed){
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// Weather for a given day: stable most of the day with small chance to shift in evening.
export function getWeather(day = getDay(), hour = getHour()){
  const season = getSeason(day);
  const rng = randFrom(dayHash(day) + 1);
  const roll = rng();

  const table = {
    spring: { clear: 0.35, cloudy: 0.25, rain: 0.30, windy: 0.10 },
    summer: { clear: 0.55, cloudy: 0.20, rain: 0.10, windy: 0.15 },
    autumn: { clear: 0.30, cloudy: 0.25, rain: 0.20, windy: 0.25 },
    winter: { clear: 0.25, cloudy: 0.25, snow: 0.35, windy: 0.15 }
  }[season];

  let cum = 0;
  let primary = 'clear';
  for (const k in table){
    cum += table[k];
    if (roll < cum){ primary = k; break; }
  }

  // late-day (after 18:00) may shift to clear for nicer sunsets
  if (hour >= 18 && rng() < 0.4) return 'clear';
  return primary;
}

export function weatherName(w){ return { clear:'clear', cloudy:'cloudy', rain:'rain', windy:'windy', snow:'snow' }[w] || w; }
export function weatherIcon(w){ return { clear:'☀️', cloudy:'☁️', rain:'🌧️', windy:'💨', snow:'🌨️' }[w] || '☀️'; }

export function getTemp(day = getDay(), hour = getHour()){
  const base = { spring: 18, summer: 28, autumn: 15, winter: 2 }[getSeason(day)];
  const dayCurve = Math.sin((Math.max(6, Math.min(20, hour)) - 6) / 14 * Math.PI);
  return Math.round(base + dayCurve * 6);
}

export function seasonColorShift(season = getSeason()){
  return {
    spring: { grass: 0x9FE070, sky: 0xC4E8F5, fog: 0xDDF6E8 },
    summer: { grass: 0x7FBF6A, sky: 0xBEE3EF, fog: 0xC9EAF5 },
    autumn: { grass: 0xD4A85A, sky: 0xE6D0A8, fog: 0xE8D6B8 },
    winter: { grass: 0xDDE8EC, sky: 0xDAE6EC, fog: 0xE2EDF2 }
  }[season];
}

// ---- event bus ----
const listeners = new Set();
export function onWeatherChange(fn){ listeners.add(fn); return () => listeners.delete(fn); }
function emit(){ listeners.forEach(fn => fn(getWeatherState())); }

let lastDay = getDay();
let lastWeather = getWeather();

export function getWeatherState(day = getDay(), hour = getHour()){
  return { season: getSeason(day), weather: getWeather(day, hour), temp: getTemp(day, hour) };
}

onTimeChange(() => {
  const d = getDay();
  const w = getWeather();
  if (d !== lastDay || w !== lastWeather){
    lastDay = d; lastWeather = w;
    emit();
  }
});

// ---- particle system for outdoor scenes ----
export class WeatherSystem {
  constructor(scene, { center = new THREE.Vector3(), radius = 24, max = 300 } = {}){
    this.scene = scene;
    this.center = center;
    this.radius = radius;
    this.group = new THREE.Group();
    this.scene.add(this.group);
    this.type = 'clear';
    this.particles = [];
    this.max = max;
    this.mesh = null;
    this.wind = new THREE.Vector3(0, 0, 0);
  }

  set(weather, season){
    if (this.type === weather && this.season === season) return;
    this.type = weather;
    this.season = season;
    this._rebuild();
  }

  _rebuild(){
    this.group.clear();
    this.particles = [];
    this.mesh = null;
    this.wind.set(0, 0, 0);

    if (this.type === 'clear'){
      if (this.season === 'spring') this._spawnDots('petal', 80, 0xFFB7C5);
      else if (this.season === 'autumn') this._spawnDots('leaf', 80, 0xD97A3E);
      return;
    }
    if (this.type === 'cloudy') return;
    if (this.type === 'rain'){
      this.wind.set(-2, 0, 0.5);
      this._spawnLines('rain', 240, 0x88AACC);
    }
    if (this.type === 'snow'){
      this.wind.set(-0.8, 0, 0.2);
      this._spawnDots('snow', 220, 0xFFFFFF);
    }
    if (this.type === 'windy'){
      this.wind.set(-5, 0, 1);
      this._spawnLines('dust', 120, 0xC9B89A);
    }
  }

  _spawnLines(kind, count, color){
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 6);
    for (let i = 0; i < count; i++){
      const p = this._rand();
      positions[i*6] = p.x; positions[i*6+1] = p.y; positions[i*6+2] = p.z;
      positions[i*6+3] = p.x; positions[i*6+4] = p.y - 0.5; positions[i*6+5] = p.z;
    }
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: kind === 'rain' ? 0.45 : 0.35 });
    this.mesh = new THREE.LineSegments(geom, mat);
    this.group.add(this.mesh);
    for (let i = 0; i < count; i++) this.particles.push({ i, kind, speed: 4 + Math.random() * 3 });
  }

  _spawnDots(kind, count, color){
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++){
      const p = this._rand();
      positions[i*3] = p.x; positions[i*3+1] = p.y; positions[i*3+2] = p.z;
    }
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({ color, size: kind === 'snow' ? 0.16 : 0.12, transparent: true, opacity: kind === 'snow' ? 0.7 : 0.55 });
    this.mesh = new THREE.Points(geom, mat);
    this.group.add(this.mesh);
    for (let i = 0; i < count; i++) this.particles.push({ i, kind, speed: 0.4 + Math.random() * 0.6, t: Math.random() * 10 });
  }

  _rand(){
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * this.radius;
    return new THREE.Vector3(
      this.center.x + Math.cos(a) * r,
      1 + Math.random() * 12,
      this.center.z + Math.sin(a) * r
    );
  }

  update(dt, target = this.center){
    if (!this.mesh) return;
    const positions = this.mesh.geometry.attributes.position.array;
    const isLines = this.type === 'rain' || this.type === 'windy';
    for (const p of this.particles){
      const i = p.i;
      if (isLines){
        const ix = i * 6;
        positions[ix]   += this.wind.x * dt;
        positions[ix+1] += this.wind.y * dt - p.speed * dt;
        positions[ix+2] += this.wind.z * dt;
        positions[ix+3] = positions[ix];
        positions[ix+4] = positions[ix+1] - (this.type === 'rain' ? 0.5 : 0.8);
        positions[ix+5] = positions[ix+2];
        if (positions[ix+1] < 0){
          const r = this._randAt(target);
          positions[ix] = r.x; positions[ix+1] = r.y; positions[ix+2] = r.z;
        }
      } else {
        const ix = i * 3;
        p.t += dt;
        positions[ix]   += this.wind.x * dt + Math.sin(p.t + i) * 0.3 * dt;
        positions[ix+1] += this.wind.y * dt - p.speed * dt;
        positions[ix+2] += this.wind.z * dt + Math.cos(p.t + i * 0.7) * 0.3 * dt;
        if (positions[ix+1] < 0){
          const r = this._randAt(target);
          positions[ix] = r.x; positions[ix+1] = r.y; positions[ix+2] = r.z;
        }
      }
    }
    this.mesh.geometry.attributes.position.needsUpdate = true;
  }

  _randAt(target){
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * this.radius;
    return new THREE.Vector3(
      target.x + Math.cos(a) * r,
      6 + Math.random() * 8,
      target.z + Math.sin(a) * r
    );
  }

  dispose(){
    this.group.clear();
    this.particles = [];
    this.mesh = null;
  }
}
